import { useEffect, useState } from 'react'
import { isProvider } from '@shared/rbac/member-roles'
import { useAuth } from '../contexts/auth-context'
import { loadMyProfile } from '../services/profile-service'

/**
 * True when the signed-in user is provider staff (any level — Basic L2,
 * Admin L3, Owner L4). Gates provider-only surfaces such as Sessions.
 *
 * Seeded synchronously from the auth user's `roleType` so providers see their
 * nav immediately and Members/Independents never see a provider-nav flash;
 * the `me` profile fetch refines the answer once it lands. Fails CLOSED: any
 * error keeps the auth-derived value (and `false` when that is unknown).
 *
 * Mirrors the established `useRoleSimplifiedNav` pattern.
 */
export function useIsProviderUser(): boolean {
  const { user } = useAuth()
  const authDerived = user ? isProvider(user) : false
  const [provider, setProvider] = useState(authDerived)

  useEffect(() => {
    setProvider(authDerived)
    let cancelled = false
    void loadMyProfile()
      .then((profile) => {
        if (!cancelled) setProvider(isProvider(profile))
      })
      .catch(() => {
        // Keep the auth-derived value; never fail open.
        if (!cancelled) setProvider(authDerived)
      })
    return () => {
      cancelled = true
    }
  }, [authDerived])

  return provider
}
